import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";

export interface BillingIdentityValue {
  businessName?: string;
  businessAddress?: string;
  businessTaxId?: string;
  businessVatId?: string;
  personalAddress?: string;
  personalTaxId?: string;
  /** Račun za isplatu; važi za oba načina isplate. */
  bankName?: string;
  bankAccount?: string;
}

interface Props {
  payoutMethod: "invoice" | "work_contract";
  isVolunteer: boolean;
  value: BillingIdentityValue;
  onChange: (next: BillingIdentityValue) => void;
  /** Da li su polja računa za isplatu obavezna (zvjezdica). Pretplata ih ne traži. */
  bankRequired?: boolean;
}

export function BillingIdentityFields({ payoutMethod, isVolunteer, value, onChange, bankRequired = true }: Props) {
  const { t } = useTranslation();

  if (isVolunteer) {
    return (
      <div className="rounded-lg bg-stone-50 border border-stone-200 p-4 text-sm text-stone-600">
        {t("billingIdentity.volunteerHint")}
      </div>
    );
  }

  const identity =
    payoutMethod === "invoice" ? (
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-stone-900">{t("billingIdentity.companyTitle")}</h4>
        <Field label={t("billingIdentity.businessName")}>
          <Input value={value.businessName ?? ""} onChange={(e) => onChange({ ...value, businessName: e.target.value })} />
        </Field>
        <Field label={t("billingIdentity.businessAddress")}>
          <Input value={value.businessAddress ?? ""} onChange={(e) => onChange({ ...value, businessAddress: e.target.value })} />
        </Field>
        <Field label={t("billingIdentity.businessTaxId")}>
          <Input
            inputMode="numeric"
            maxLength={13}
            value={value.businessTaxId ?? ""}
            onChange={(e) => onChange({ ...value, businessTaxId: e.target.value })}
          />
        </Field>
        <Field label={t("billingIdentity.businessVatId")}>
          <Input value={value.businessVatId ?? ""} onChange={(e) => onChange({ ...value, businessVatId: e.target.value })} />
        </Field>
      </div>
    ) : (
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-stone-900">{t("billingIdentity.personalTitle")}</h4>
        <Field label={t("billingIdentity.personalAddress")}>
          <Input value={value.personalAddress ?? ""} onChange={(e) => onChange({ ...value, personalAddress: e.target.value })} />
        </Field>
        <Field label={t("billingIdentity.personalTaxId")}>
          <Input
            inputMode="numeric"
            maxLength={13}
            value={value.personalTaxId ?? ""}
            onChange={(e) => onChange({ ...value, personalTaxId: e.target.value })}
          />
        </Field>
      </div>
    );

  return (
    <div className="space-y-6">
      {identity}
      <BankAccountFields value={value} onChange={onChange} required={bankRequired} />
    </div>
  );
}

/** Račun za isplatu: isti blok za obrt i ugovor o djelu, jer novac ide na ovaj račun. */
function BankAccountFields({
  value,
  onChange,
  required,
}: {
  value: BillingIdentityValue;
  onChange: (next: BillingIdentityValue) => void;
  required: boolean;
}) {
  const { t } = useTranslation();
  const mark = required ? " *" : "";
  return (
    <div className="space-y-3">
      <div>
        <h4 className="text-sm font-semibold text-stone-900">{t("billingIdentity.bankTitle")}</h4>
        <p className="mt-0.5 text-sm text-stone-600">{t("billingIdentity.bankDescription")}</p>
      </div>
      <Field label={t("billingIdentity.bankName") + mark}>
        <Input
          autoComplete="off"
          maxLength={120}
          value={value.bankName ?? ""}
          onChange={(e) => onChange({ ...value, bankName: e.target.value })}
        />
      </Field>
      <Field label={t("billingIdentity.bankAccount") + mark} hint={t("billingIdentity.bankAccountHint")}>
        <Input
          autoComplete="off"
          maxLength={42}
          value={value.bankAccount ?? ""}
          onChange={(e) => onChange({ ...value, bankAccount: e.target.value })}
        />
      </Field>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-stone-700">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-stone-500">{hint}</span>}
    </label>
  );
}
